import React, { useEffect, useMemo, useRef, useState } from 'react';
import GenderInput from './GenderInput';
import { calculateDateSelection } from '../services/api';
import { localizeChineseText } from '../services/chineseText';

const eventOptions = [
  ['marriage', 'dateEventMarriage'],
  ['moving', 'dateEventMoving'],
  ['opening', 'dateEventOpening'],
  ['contract', 'dateEventContract'],
  ['travel', 'dateEventTravel'],
  ['worship', 'dateEventWorship'],
  ['custom', 'dateEventCustom'],
];

const roleKeysByEvent = {
  marriage: ['dateRoleGroom', 'dateRoleBride'],
  moving: ['dateRoleHomeowner'],
  opening: ['dateRoleBoss'],
  contract: ['dateRoleSigner'],
  travel: ['dateRoleTraveler'],
  worship: ['dateRoleDevotee'],
  custom: ['dateRolePerson'],
};

const roleKeyForPerson = (eventType, index) => (
  roleKeysByEvent[eventType]?.[index] || 'dateRolePerson'
);

const newPerson = (roleKey = 'dateRolePerson', translate = (key) => key) => ({
  role: roleKey ? translate(roleKey) : '',
  prefilledRoleKey: roleKey,
  birth_date: '',
  birth_time: '12:00',
  is_male: true,
});

const createInitialForm = (translate) => ({
  event_type: 'marriage',
  custom_event: '',
  start_date: '',
  end_date: '',
  people: [
    newPerson(roleKeyForPerson('marriage', 0), translate),
    newPerson(roleKeyForPerson('marriage', 1), translate),
  ],
});

const parseDate = (value) => {
  const rawDate = String(value || '').trim();
  const isoDateMatch = rawDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const legacyDateMatch = rawDate.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!isoDateMatch && !legacyDateMatch) return null;
  const year = isoDateMatch ? Number(isoDateMatch[1]) : Number(legacyDateMatch[3]);
  const month = isoDateMatch ? Number(isoDateMatch[2]) : Number(legacyDateMatch[2]);
  const day = isoDateMatch ? Number(isoDateMatch[3]) : Number(legacyDateMatch[1]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return { year, month, day, iso: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` };
};

const parseTime = (value) => {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return { hour, minute };
};

const DateSelectionPage = ({ t, lang = 'zh-TW' }) => {
  const [form, setForm] = useState(() => createInitialForm(t));
  const [result, setResult] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dateScrollRef = useRef(null);
  const txt = (value) => localizeChineseText(value || '', lang);

  const minPeople = form.event_type === 'marriage' ? 2 : 1;
  const selected = selectedDate || result?.best_dates?.[0];

  const updatePerson = (index, changes) => {
    const people = form.people.map((person, personIndex) => (
      personIndex === index ? { ...person, ...changes } : person
    ));
    setForm({ ...form, people });
  };

  const handleEventChange = (eventType) => {
    setForm((current) => {
      const currentPeople = current.people.length ? current.people : [newPerson(roleKeyForPerson(eventType, 0), t)];
      const minCount = eventType === 'marriage' ? 2 : 1;
      const people = Array.from({ length: Math.max(currentPeople.length, minCount) }, (_, index) => {
        const person = currentPeople[index];
        const nextRoleKey = roleKeyForPerson(eventType, index);
        if (!person) return newPerson(nextRoleKey, t);
        if (!person.prefilledRoleKey) return person;
        return {
          ...person,
          role: t(nextRoleKey),
          prefilledRoleKey: nextRoleKey,
        };
      });
      return { ...current, event_type: eventType, people };
    });
  };

  const addPerson = () => setForm((current) => ({
    ...current,
    people: [...current.people, newPerson(roleKeyForPerson(current.event_type, current.people.length), t)],
  }));

  useEffect(() => {
    setForm((current) => ({
      ...current,
      people: current.people.map((person) => (
        person.prefilledRoleKey
          ? { ...person, role: t(person.prefilledRoleKey) }
          : person
      )),
    }));
  }, [lang, t]);

  const scrollDateCards = (direction) => {
    const node = dateScrollRef.current;
    if (!node) return;
    const distance = Math.max(node.clientWidth * 0.82, 260);
    node.scrollBy({ left: direction * distance, behavior: 'smooth' });
  };

  const removePerson = (index) => {
    if (form.people.length <= minPeople) return;
    setForm({ ...form, people: form.people.filter((_, personIndex) => personIndex !== index) });
  };

  const parsedPeople = useMemo(() => form.people.map((person) => {
    const birth = parseDate(person.birth_date);
    const time = parseTime(person.birth_time || '12:00');
    if (!birth || !time || !person.role.trim()) return null;
    return {
      role: person.role.trim(),
      birth_date: birth.iso,
      hour: time.hour,
      minute: time.minute,
      is_male: person.is_male,
    };
  }).filter(Boolean), [form.people]);

  const submit = async (event) => {
    event.preventDefault();
    const start = parseDate(form.start_date);
    const end = parseDate(form.end_date);
    if (!start || !end || parsedPeople.length < minPeople || (form.event_type === 'custom' && !form.custom_event.trim())) {
      setError(form.event_type === 'marriage' ? t('dateMarriageValidationError') : t('dateValidationError'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const computed = await calculateDateSelection({
        event_type: form.event_type,
        custom_event: form.custom_event,
        start_date: start.iso,
        end_date: end.iso,
        people: parsedPeople,
        lang,
      });
      setResult(computed);
      setSelectedDate(computed.best_dates?.[0] || null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.detail || t('dateCalcError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bz-date-page">
      <section className="bz-intro bz-intro--compact fc-rise">
        <a className="fc-btn fc-btn--ghost fc-btn--sm" href="/bazi/">{t('backToIntegrated')}</a>
        <h1 className="bz-intro__title">{t('datePageTitle')}</h1>
        <p className="bz-intro__desc">{t('datePageDesc')}</p>
      </section>

      {!result && (
        <div className="bz-form-card date-form-card">
          <form className="bz-form date-form" onSubmit={submit}>
            <section className="fc-card date-form-section">
              <h2>{t('dateEventTitle')}</h2>
              <div className="date-event-grid">
                <div className="fc-field">
                  <label className="fc-label" htmlFor="date-event-type">{t('dateEventLabel')}</label>
                  <select
                    id="date-event-type"
                    className="fc-select"
                    value={form.event_type}
                    onChange={(event) => handleEventChange(event.target.value)}
                  >
                    {eventOptions.map(([value, label]) => <option value={value} key={value}>{t(label)}</option>)}
                  </select>
                </div>
                {form.event_type === 'custom' && (
                  <div className="fc-field">
                    <label className="fc-label" htmlFor="date-custom-event">{t('dateCustomEventLabel')}</label>
                    <input
                      id="date-custom-event"
                      className="fc-input"
                      value={form.custom_event}
                      onChange={(event) => setForm({ ...form, custom_event: event.target.value })}
                      placeholder={t('dateCustomEventPlaceholder')}
                    />
                  </div>
                )}
              </div>
            </section>

            <section className="fc-card date-form-section">
              <h2>{t('dateRangeTitle')}</h2>
              <div className="compat-date-grid">
                <div className="fc-field">
                  <label className="fc-label" htmlFor="date-start">{t('dateStartLabel')}</label>
                  <input
                    id="date-start"
                    className="fc-input"
                    type="date"
                    min="1900-01-01"
                    max="2100-12-31"
                    value={form.start_date}
                    onChange={(event) => setForm({ ...form, start_date: event.target.value })}
                  />
                </div>
                <div className="fc-field">
                  <label className="fc-label" htmlFor="date-end">{t('dateEndLabel')}</label>
                  <input
                    id="date-end"
                    className="fc-input"
                    type="date"
                    min="1900-01-01"
                    max="2100-12-31"
                    value={form.end_date}
                    onChange={(event) => setForm({ ...form, end_date: event.target.value })}
                  />
                </div>
              </div>
              <p className="bz-note">{t('dateRangeHint')}</p>
            </section>

            <section className="fc-card date-form-section">
              <div className="date-section-head">
                <div>
                  <h2>{t('datePeopleTitle')}</h2>
                  <p>{form.event_type === 'marriage' ? t('dateMarriagePeopleHint') : t('datePeopleHint')}</p>
                </div>
                <button type="button" className="fc-btn fc-btn--secondary fc-btn--sm" onClick={addPerson}>
                  {t('dateAddPerson')}
                </button>
              </div>

              <div className="date-people-list">
                {form.people.map((person, index) => (
                  <article className="date-person-card" key={index}>
                    <div className="date-person-card__top">
                      <strong>{t('datePerson')} {index + 1}</strong>
                      <button
                        type="button"
                        className="fc-btn fc-btn--ghost fc-btn--sm"
                        onClick={() => removePerson(index)}
                        disabled={form.people.length <= minPeople}
                      >
                        {t('dateRemovePerson')}
                      </button>
                    </div>
                    <div className="date-person-grid">
                      <div className="fc-field">
                        <label className="fc-label">{t('dateRoleLabel')}</label>
                        <input
                          className="fc-input"
                          value={person.role}
                          onChange={(event) => updatePerson(index, { role: event.target.value, prefilledRoleKey: null })}
                          placeholder={t('dateRolePlaceholder')}
                        />
                      </div>
                      <div className="fc-field">
                        <label className="fc-label">{t('compatBirthDateLabel')}</label>
                        <input
                          className="fc-input"
                          type="date"
                          min="1900-01-01"
                          max="2100-12-31"
                          value={person.birth_date}
                          onChange={(event) => updatePerson(index, { birth_date: event.target.value })}
                        />
                      </div>
                      <div className="fc-field">
                        <label className="fc-label" htmlFor={`date-person-${index}-birth-time`}>{t('compatBirthTimeLabel')}</label>
                        <input
                          id={`date-person-${index}-birth-time`}
                          className="fc-input"
                          type="time"
                          step="60"
                          value={person.birth_time}
                          onChange={(event) => updatePerson(index, { birth_time: event.target.value })}
                          placeholder={t('compatBirthTimePlaceholder')}
                        />
                      </div>
                    </div>
                    <GenderInput value={person.is_male} onChange={(value) => updatePerson(index, { is_male: value })} t={t} />
                  </article>
                ))}
              </div>
            </section>

            {error && <div className="fc-error" role="alert">{txt(error)}</div>}
            <div className="bz-form__actions">
              <button type="submit" className="fc-btn fc-btn--primary fc-btn--lg" disabled={loading}>
                {loading ? t('dateCalculating') : t('dateCalculate')}
              </button>
              <button type="button" className="fc-btn fc-btn--ghost" onClick={() => setForm({
                ...createInitialForm(t),
              })}>
                {t('reset')}
              </button>
            </div>
          </form>
        </div>
      )}

      {result && (
        <section className="bz-result date-result">
          <div className="bz-recap">
            <div className="bz-recap__info">
              <span className="fc-eyebrow">{t('dateEventTitle')}</span>
              <span className="bz-recap__value fc-num">
                {txt(result.event?.title)}
                <span className="bz-recap__sep">·</span>
                {result.range?.start} — {result.range?.end}
              </span>
            </div>
            <button type="button" className="fc-btn fc-btn--secondary fc-btn--sm" onClick={() => setResult(null)}>
              {t('dateEdit')}
            </button>
          </div>

          <section className="bz-block">
            <div className="fc-card date-hero">
              <div>
                <span className="fc-eyebrow">{t('dateBestTitle')}</span>
                <h2>{selected?.date} · {txt(selected?.grade)}</h2>
                <p>{txt(selected?.reasons?.[0] || selected?.warnings?.[0] || result.method?.principle)}</p>
              </div>
              <div className="compat-score" aria-label={`${t('score')} ${selected?.score}`}>
                <strong>{selected?.score}</strong>
                <span>{t('score')}</span>
              </div>
            </div>
          </section>

          <section className="bz-block">
            <div className="date-scroll-head">
              <h2 className="bz-section-title">{t('dateTopDatesTitle')}</h2>
              <div className="date-scroll-actions" aria-label={t('dateScrollControls')}>
                <button type="button" className="fc-btn fc-btn--ghost fc-btn--sm" onClick={() => scrollDateCards(-1)} aria-label={t('dateScrollPrev')}>‹</button>
                <button type="button" className="fc-btn fc-btn--ghost fc-btn--sm" onClick={() => scrollDateCards(1)} aria-label={t('dateScrollNext')}>›</button>
              </div>
            </div>
            <div className="date-card-scroll" ref={dateScrollRef} tabIndex="0" aria-label={t('dateTopDatesTitle')}>
              {result.best_dates?.map((day) => (
                <button
                  type="button"
                  className={`date-choice-card ${selected?.date === day.date ? 'is-active' : ''}`}
                  key={day.date}
                  onClick={() => setSelectedDate(day)}
                >
                  <span className="fc-eyebrow">{t('dateWeekday')} {txt(day.weekday)}</span>
                  <strong>{day.date}</strong>
                  <span>{txt(day.grade)} · {day.score}</span>
                </button>
              ))}
            </div>
          </section>

          {selected && (
            <section className="bz-block">
              <h2 className="bz-section-title">{t('dateSelectedDetailTitle')}</h2>
              <div className="bz-grid-2">
                <article className="fc-card date-detail-card">
                  <span className="fc-eyebrow">{t('dateCalendarSigns')}</span>
                  <div className="fc-defs">
                    <div className="fc-def"><span className="fc-def__key">{t('dateLunarDay')}</span><span className="fc-def__val">{txt(selected.lunar?.year)} · {txt(selected.lunar?.month)} · {txt(selected.lunar?.day)}</span></div>
                    <div className="fc-def"><span className="fc-def__key">{t('dateOfficer')}</span><span className="fc-def__val">{txt(selected.zhi_xing)}</span></div>
                    <div className="fc-def"><span className="fc-def__key">{t('dateTianShen')}</span><span className="fc-def__val">{txt(selected.tian_shen)} · {txt(selected.tian_shen_type)}</span></div>
                    <div className="fc-def"><span className="fc-def__key">{t('dateXiu')}</span><span className="fc-def__val">{txt(selected.xiu)} · {txt(selected.xiu_luck)}</span></div>
                    <div className="fc-def"><span className="fc-def__key">{t('dateChong')}</span><span className="fc-def__val">{txt(selected.day_chong_desc)} · {txt(selected.day_sha)}</span></div>
                  </div>
                </article>

                <article className="fc-card date-detail-card">
                  <span className="fc-eyebrow">{t('dateReasonTitle')}</span>
                  <ul className="bz-modern-list">
                    {(selected.reasons?.length ? selected.reasons : [t('dateNoMajorGood')]).map((item, index) => <li key={`r-${index}`}>{txt(item)}</li>)}
                  </ul>
                  <span className="fc-eyebrow">{t('dateWarningTitle')}</span>
                  <ul className="bz-modern-list">
                    {(selected.warnings?.length ? selected.warnings : [t('dateNoMajorWarning')]).map((item, index) => <li key={`w-${index}`}>{txt(item)}</li>)}
                  </ul>
                </article>
              </div>
            </section>
          )}

          <section className="bz-block">
            <h2 className="bz-section-title">{t('datePeopleResultTitle')}</h2>
            <div className="date-related-grid">
              {result.people?.map((person, index) => (
                <article className="compat-sign-card" key={`${person.role}-${index}`}>
                  <span className="fc-eyebrow">{txt(person.role)}</span>
                  <strong>{person.birth_date} {person.birth_time}</strong>
                  <p>{t('zodiac')}：{txt(person.zodiac)} · {t('dayPillar')}：{txt(person.pillars?.day)}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="bz-block">
            <h2 className="bz-section-title">{t('dateAllDatesTitle')}</h2>
            <div className="date-table-wrap">
              <table className="date-table">
                <thead>
                  <tr>
                    <th>{t('date')}</th>
                    <th>{t('score')}</th>
                    <th>{t('dateGrade')}</th>
                    <th>{t('dateYiMatch')}</th>
                    <th>{t('dateJiMatch')}</th>
                    <th>{t('dateChong')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.all_dates?.map((day) => (
                    <tr key={day.date}>
                      <td>{day.date}</td>
                      <td>{day.score}</td>
                      <td>{txt(day.grade)}</td>
                      <td>{txt(day.yi_matches?.join('、')) || t('none')}</td>
                      <td>{txt(day.ji_matches?.join('、')) || t('none')}</td>
                      <td>{txt(day.day_chong_desc)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </section>
      )}
    </div>
  );
};

export default DateSelectionPage;
